import { useId } from '@reach/auto-id';
import classnames from 'classnames2';
import React, { Children, cloneElement, useEffect, useRef, useState } from 'react';

import { IBaseProps } from '../utils';

import { IPopperProps, Popper } from '../Popper';

import styles from './styles.module.scss';

const wrapEvent = (child, theirHandler, ourHandler) => (event) => {
  if (typeof child !== 'string' && child.props[theirHandler]) {
    child.props[theirHandler](event);
  }

  if (!event.defaultPrevented) {
    return ourHandler(event);
  }
};

export interface ITooltipProps extends IBaseProps {
  /** An alternate label for screen readers. */
  ariaLabel?: string;
  /** The delay in ms for the tooltip to show. */
  showDelay?: number;
  /** The delay in ms for the tooltip to hide. */
  hideDelay?: number;
  /** Determines the top offset of the tooltip. Please don't change unless absolutely necessary. */
  topOffset?: number;
  /** Determines the right offset of the tooltip. Please don't change unless absolutely necessary. */
  rightOffset?: number;
  /** if this is true the tooltip will be hided */
  hide?: boolean;
  /** Sets the content of the tooltip. Can be a string or a React component. */
  content: string | React.ReactNode;
  /** Positions the tooltip relative to its reference element. */
  placement?: IPopperProps['placement'];
  /** If true, the tooltip will wrap the children in a span with tabIndex=0 */
  shouldWrapChildren?: boolean;
  onOpen?(): void;
  onClose?(): void;
}

export const Tooltip: React.FC<ITooltipProps> = ({
  className,
  content,
  ariaLabel,
  showDelay = 0,
  hideDelay = 0,
  topOffset = 0,
  rightOffset = 0,
  hide = false,
  placement = 'top',
  children,
  shouldWrapChildren,
  dataTest,
  onOpen: onOpenProp,
  onClose: onCloseProp
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const tooltipId = `tooltip-${useId()}`;
  const referenceRef = useRef();
  let enterTimeout = null;
  let exitTimeout = null;

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const openWithDelay = () => {
    enterTimeout = setTimeout(onOpen, showDelay);
  };

  const closeWithDelay = () => {
    clearTimeout(enterTimeout);
    exitTimeout = setTimeout(onClose, hideDelay);
  };

  const handleOpen = () => {
    openWithDelay();

    if (onOpenProp) {
      onOpenProp();
    }
  };

  const handleClose = () => {
    closeWithDelay();

    if (onCloseProp) {
      onCloseProp();
    }
  };

  const handleMouseEnter = () => {
    clearTimeout(exitTimeout);
  };

  const handleMouseLeave = () => {
    setTimeout(onClose, 0);
  };

  const onKeyDown = (event) => {
    if (event.keyCode === 27) {
      handleClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const referenceProps = {
    ref: referenceRef,
    onMouseEnter: wrapEvent(children, 'onMouseEnter', handleOpen),
    onMouseLeave: wrapEvent(children, 'onMouseLeave', handleClose),
    onFocus: wrapEvent(children, 'onFocus', handleOpen),
    onBlur: wrapEvent(children, 'onBlur', handleClose),
    ...(isOpen && { 'aria-describedby': tooltipId })
  };

  let clone;

  if (typeof children === 'string' || shouldWrapChildren) {
    clone = (
      <span tabIndex={0} {...referenceProps}>
        {children}
      </span>
    );
  } else {
    clone = cloneElement(Children.only(children) as any, referenceProps);
  }

  const hasAriaLabel = ariaLabel != null;

  return (
    <>
      {clone}
      <Popper
        className={classnames(styles.popper, className)}
        usePortal={true}
        modifiers={{ offset: { offset: `${topOffset},${rightOffset}` } }}
        isOpen={isOpen && !hide}
        placement={placement}
        anchorEl={referenceRef.current}
        id={hasAriaLabel ? undefined : tooltipId}
        role={hasAriaLabel ? undefined : 'tooltip'}
        dataTest={dataTest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.tooltip}>{content}</div>
        {hasAriaLabel && (
          <div role="tooltip" id={tooltipId} className={styles.visuallyHidden}>
            {ariaLabel}
          </div>
        )}
      </Popper>
    </>
  );
};
